import { ArrowLeft, Globe } from "@phosphor-icons/react";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { useActivityDataDetail } from "../api/activity-data";
import AppLink from "../components/AppLink";
import ContentContainer from "../components/ContentContainer";
import IconButton from "../components/IconButton";
import MainLayout from "../components/MainLayout";
import Separator from "../components/Separator";
import Typography from "../components/Typography";
import { appPaths } from "../Routes";
import { RequireEmissionsSourceEvidenceTable } from "../components/RequiredEvidenceTable";
import SectionTitle from "../components/SectionTitle";
import ActivityDataCalculationCard from "../components/ActivityDataCalculationCard";
import { useEmissionsSourceById } from "../api/emissions-sources";
import { ActivityDataDetail } from "../api/models/ActivityData";

const ActivityDataDetailView = ({
    activityData,
}: {
    activityData: ActivityDataDetail;
}): JSX.Element => {
    const { emissionsSource, isLoading } = useEmissionsSourceById(
        activityData.emissionsSourceId
    );

    if (isLoading) return <></>;

    return (
        <ActivityDataCalculationCard
            {...activityData}
            dataCollectionType={emissionsSource?.dataStatusId}
        />
    );
};

const ActivityDataPointPage = () => {
    const { id } = useParams();
    const { activityData, isLoading } = useActivityDataDetail(Number(id));

    if (!id) return null;

    return (
        <MainLayout isLoading={isLoading}>
            {!activityData && (
                <ContentContainer className="py-10 text-center">
                    <p>No activity data found matching this id</p>
                </ContentContainer>
            )}
            {activityData && (
                <ContentContainer className="pt-14 pb-11 max-w-7xl">
                    <div className="flex flex-col gap-6 sm:flex-row sm:items-center pb-5">
                        <div className="flex items-center">
                            <IconButton
                                label="Back"
                                icon={ArrowLeft}
                                iconSize="large"
                                className="mr-3"
                                onClick={() => window.history.back()}
                            />
                            <Typography
                                variant="headline-small"
                                component="h1"
                                className="mr-3"
                            >
                                Asset Data Point
                            </Typography>
                        </div>
                        <div className="flex items-center flex-1 justify-between">
                            <div className="ml-auto flex items-center gap-2 font-semibold">
                                <Globe weight="light" size={24} />
                                <Typography
                                    variant="headline-small"
                                    component="span"
                                    className="text-deep-ocean-20"
                                >
                                    Scope {activityData?.scope}
                                </Typography>
                            </div>
                        </div>
                    </div>
                    <Separator className="mb-8" />
                    <Typography variant="body-large" component="p">
                        <b>Activity Period:</b>{" "}
                        {format(
                            new Date(activityData.activityStartDate),
                            "dd/MM/yyyy"
                        )}{" "}
                        -{" "}
                        {format(
                            new Date(activityData.activityEndDate),
                            "dd/MM/yyyy"
                        )}
                    </Typography>
                    <Typography variant="body-large" component="p">
                        <b>Location:</b> {activityData.locationName}
                    </Typography>
                    <Typography variant="body-large" component="p">
                        <b>Asset:</b>{" "}
                        <AppLink
                            to={appPaths.emissionsSourceDetail(
                                activityData.emissionsSourceId.toString()
                            )}
                        >
                            {activityData.dataSourceName}
                        </AppLink>
                    </Typography>
                    <SectionTitle title="Emissions" />
                    <ActivityDataDetailView activityData={activityData} />
                    <RequireEmissionsSourceEvidenceTable
                        defaultStatusFilter="All"
                        emissionsSourceId={activityData.emissionsSourceId}
                        activityDataId={activityData.id}
                        noEvidenceMessage="No evidence found"
                        readonly={true}
                        title="Asset Evidence"
                    />
                </ContentContainer>
            )}
        </MainLayout>
    );
};

export default ActivityDataPointPage;
